import React, { useEffect, useState } from 'react';
import { Workbook } from '@fortune-sheet/react';
import '@fortune-sheet/react/dist/index.css';
import { transformExcelToLucky } from 'luckyexcel';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { Box, CircularProgress, Typography } from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// ── Strip EMF files from the xlsx/xlsm ZIP before luckyexcel processes it ────
// EMF (Enhanced Metafile Format) images stored in xl/media/ crash luckyexcel's
// internal parser with "Cannot read properties of undefined (reading 't')".
// Removing them lets the rest of the file (PNG/JPG images, cell data, charts)
// load correctly. The drawing XML references that pointed to removed EMFs will
// simply produce no-op image slots.
async function stripEmfFromBuffer(arrayBuffer) {
	const zip = await JSZip.loadAsync(arrayBuffer);
	let removed = 0;
	zip.forEach((path) => {
		if (/^xl\/media\/.+\.emf$/i.test(path)) {
			zip.remove(path);
			removed++;
		}
	});
	if (removed === 0) return arrayBuffer; // nothing to strip — return original
	console.info(`[SpreadsheetViewer] Stripped ${removed} EMF file(s) from ZIP`);
	const cleanBuffer = await zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' });
	return cleanBuffer;
}

// ── Helpers for the XLSX fallback table ───────────────────────────────────────
const xlColor = (argb) => {
	if (!argb || argb.length < 6) return null;
	const hex = argb.length === 8 ? argb.slice(2) : argb;
	return hex.toUpperCase() === 'FFFFFF' ? null : `#${hex}`;
};

const parseWithXlsx = (arrayBuffer) => {
	const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true, cellDates: true });
	const sheetNames = workbook.SheetNames;
	const sheetsData = {};

	sheetNames.forEach((name) => {
		const ws = workbook.Sheets[name];
		if (!ws || !ws['!ref']) { sheetsData[name] = { rows: [], numCols: 0, colWidths: [] }; return; }
		const range = XLSX.utils.decode_range(ws['!ref']);
		const numCols = range.e.c - range.s.c + 1;
		const colWidths = (ws['!cols'] || []).map((c) => (c?.wch ? Math.max(c.wch * 7, 60) : 80));
		while (colWidths.length < numCols) colWidths.push(80);

		const rows = [];
		for (let r = range.s.r; r <= range.e.r; r++) {
			const row = [];
			for (let c = range.s.c; c <= range.e.c; c++) {
				const ref = XLSX.utils.encode_cell({ r, c });
				const cell = ws[ref];
				const s = cell?.s || {};
				row.push({
					value: cell ? XLSX.utils.format_cell(cell) : '',
					bold: !!s?.font?.bold,
					italic: !!s?.font?.italic,
					underline: !!s?.font?.underline,
					bgColor: xlColor(s?.fill?.fgColor?.rgb),
					color: xlColor(s?.font?.color?.rgb),
					align: s?.alignment?.horizontal || null,
				});
			}
			rows.push(row);
		}
		sheetsData[name] = { rows, numCols, colWidths };
	});

	return { sheetNames, sheetsData };
};

// ── Fallback table renderer (used when luckyexcel fully fails) ────────────────
const FallbackTable = ({ data, isDark }) => {
	const [active, setActive] = useState(data.sheetNames[0]);
	const sheet = data.sheetsData[active] || { rows: [], numCols: 0, colWidths: [] };
	const colLetters = Array.from({ length: sheet.numCols }, (_, i) => XLSX.utils.encode_col(i));
	const border = isDark ? '#3a3a3a' : '#d0d0d0';

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: '0', overflow: 'hidden', border: 'none' }}>
			{/* Notice banner */}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.8, background: isDark ? '#2a2000' : '#fffbea', borderBottom: `1px solid ${isDark ? '#554400' : '#ffe58f'}`, flexShrink: 0 }}>
				<WarningAmberIcon sx={{ fontSize: 15, color: '#d4a017' }} />
				<Typography sx={{ fontSize: '11px', color: isDark ? '#f5c842' : '#7a5c00' }}>
					Images and EMF graphics could not be rendered. All cell data is shown below.
				</Typography>
			</Box>

			{/* Grid */}
			<Box sx={{ flex: 1, overflow: 'auto' }}>
				<table style={{ borderCollapse: 'collapse', fontFamily: 'Calibri, Segoe UI, Arial, sans-serif', fontSize: '13px', tableLayout: 'fixed', minWidth: '100%' }}>
					<thead>
						<tr>
							<th style={{ width: 46, minWidth: 46, position: 'sticky', top: 0, left: 0, zIndex: 3, background: isDark ? '#2d2d2d' : '#f0f0f0', border: `1px solid ${border}` }} />
							{colLetters.map((l, i) => (
								<th key={i} style={{ width: sheet.colWidths[i] || 80, minWidth: sheet.colWidths[i] || 80, position: 'sticky', top: 0, zIndex: 2, background: isDark ? '#2d2d2d' : '#f0f0f0', border: `1px solid ${border}`, padding: '3px 4px', textAlign: 'center', fontWeight: 600, fontSize: '12px', color: isDark ? '#ccc' : '#444', userSelect: 'none' }}>
									{l}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{sheet.rows.map((row, ri) => (
							<tr key={ri}>
								<td style={{ position: 'sticky', left: 0, zIndex: 1, background: isDark ? '#2d2d2d' : '#f0f0f0', border: `1px solid ${border}`, padding: '2px 6px', textAlign: 'right', fontSize: '12px', color: isDark ? '#aaa' : '#666', userSelect: 'none', minWidth: 46 }}>
									{ri + 1}
								</td>
								{row.map((cell, ci) => (
									<td key={ci} style={{
										border: `1px solid ${border}`,
										padding: '2px 6px',
										backgroundColor: cell.bgColor || (isDark ? '#1e1e1e' : '#fff'),
										color: cell.color || (isDark ? '#e0e0e0' : '#000'),
										fontWeight: cell.bold ? 'bold' : 'normal',
										fontStyle: cell.italic ? 'italic' : 'normal',
										textDecoration: cell.underline ? 'underline' : 'none',
										textAlign: cell.align || 'left',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										maxWidth: sheet.colWidths[ci] || 80,
									}}>
										{cell.value}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</Box>

			{/* Sheet tabs */}
			<Box sx={{ display: 'flex', borderTop: `1px solid ${isDark ? '#444' : '#c6c6c6'}`, background: isDark ? '#252525' : '#ededed', flexShrink: 0, overflowX: 'auto' }}>
				{data.sheetNames.map((name) => (
					<Box key={name} onClick={() => setActive(name)} sx={{
						px: 2, py: 0.6, cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap', userSelect: 'none',
						fontWeight: active === name ? 600 : 400,
						background: active === name ? (isDark ? '#1e1e1e' : '#fff') : 'transparent',
						color: active === name ? '#217346' : (isDark ? '#aaa' : '#555'),
						borderTop: active === name ? '2px solid #217346' : '2px solid transparent',
						borderRight: `1px solid ${isDark ? '#444' : '#c6c6c6'}`,
						'&:hover': { background: isDark ? '#333' : '#e0ede6', color: '#217346' },
					}}>
						{name}
					</Box>
				))}
			</Box>
		</Box>
	);
};

// ── Main component ─────────────────────────────────────────────────────────────
const LUCKYEXCEL_TIMEOUT_MS = 12000;

const SpreadsheetViewer = ({ arrayBuffer, fileName, isDark }) => {
	const [sheets, setSheets] = useState(null);        // fortune-sheet data
	const [fallback, setFallback] = useState(null);    // XLSX table fallback data
	const [loadError, setLoadError] = useState(null);  // only if even XLSX fails

	useEffect(() => {
		if (!arrayBuffer || !fileName) return;
		setSheets(null);
		setFallback(null);
		setLoadError(null);

		let settled = false;

		const activateFallback = (reason) => {
			if (settled) return;
			settled = true;
			console.warn('[SpreadsheetViewer] Falling back to XLSX table:', reason);
			try {
				const data = parseWithXlsx(arrayBuffer);
				setFallback(data);
			} catch (e) {
				setLoadError('Could not load spreadsheet: ' + e.message);
			}
		};

		// Timeout guard — if luckyexcel hangs and never calls the callback
		const timer = setTimeout(() => activateFallback('timeout'), LUCKYEXCEL_TIMEOUT_MS);

		// Strip EMF images first (they crash luckyexcel's parser), then convert
		stripEmfFromBuffer(arrayBuffer)
			.then((cleanBuffer) => {
				if (settled) return;
				const blob = new Blob([cleanBuffer], { type: 'application/vnd.ms-excel.sheet.macroenabled.12' });
				const file = new File([blob], fileName);

				transformExcelToLucky(file, (exportJson) => {
					if (settled) return;
					clearTimeout(timer);
					settled = true;

					if (!exportJson?.sheets?.length) {
						activateFallback('empty result from luckyexcel');
						return;
					}
					// luckyexcel stores images as a keyed object {id: imgData};
					// fortune-sheet's ImgBoxs calls .map() so it must be an array.
					const normalized = exportJson.sheets.map((sheet) => ({
						...sheet,
						images: sheet.images && !Array.isArray(sheet.images)
							? Object.values(sheet.images)
							: (sheet.images || []),
					}));
					setSheets(normalized);
				});
			})
			.catch((err) => {
				clearTimeout(timer);
				activateFallback(err.message);
			});

		return () => clearTimeout(timer);
	}, [arrayBuffer, fileName]);

	// Still loading
	if (!sheets && !fallback && !loadError) {
		return (
			<Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, borderRadius: '0', border: 'none', background: isDark ? '#1e1e1e' : '#fff' }}>
				<CircularProgress size={28} sx={{ color: '#27ae60' }} />
				<Typography sx={{ fontSize: '13px', color: isDark ? '#aaa' : '#666' }}>
					Loading spreadsheet…
				</Typography>
			</Box>
		);
	}

	// Complete failure (even XLSX couldn't parse)
	if (loadError) {
		return (
			<Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, borderRadius: '0', border: 'none', background: isDark ? '#1e1e1e' : '#fff' }}>
				<TableChartIcon sx={{ fontSize: 48, color: '#27ae60', opacity: 0.4 }} />
				<Typography sx={{ fontSize: '14px', color: isDark ? '#aaa' : '#666' }}>{loadError}</Typography>
			</Box>
		);
	}

	// XLSX fallback (EMF crash or luckyexcel failure)
	if (fallback) {
		return <FallbackTable data={fallback} isDark={isDark} />;
	}

	// Fortune-sheet full render (images, charts, merged cells, freeze panes)
	return (
		<Box sx={{ width: '100%', height: '100%', position: 'relative', borderRadius: '0', overflow: 'hidden', border: 'none' }}>
			<Workbook
				data={sheets}
				showToolbar={false}
				showFormulaBar={false}
				showSheetTabs
				allowEdit={false}
			/>
		</Box>
	);
};

export default SpreadsheetViewer;
